import type { PassengerRideView, RideStatus } from '@limuzin/passenger';
import { alertsOn } from './alerts';
import { ACCEPTED, moment } from './sounds';

/**
 * What the passenger is told in the system's notifications while the app is open in
 * a tab they are not looking at, or on a screen other than the conversation: the
 * driver wrote in the ride's chat, or «Поддержка» answered.
 *
 * What arrives here is whatever the socket heard, and the notification is made in the
 * page. A browser with no tab in front of anybody is told the same things by Web Push
 * instead — see `./push` and the `push` handler in `public/sw.js` (ADR 0027) — under
 * the same tags, so either one replaces the other rather than stacking. Where there
 * is no push, a closed tab hears nothing, and the passenger finds the dot on «Новый
 * заказ» or «Профиль» on coming back.
 *
 * Shown through the service worker where there is one, because Chrome on Android
 * refuses `new Notification()` outright and only a worker's `showNotification` works
 * there. A tap comes back to the page as a message from the worker — see `public/sw.js`
 * — naming where to go, which is a hash route: `#/?chat=<ride>` or
 * `#/profile?thread=<thread>`.
 */

/** Where a notification's tap leads: a ride's chat, or one Обращение. */
export const chatRoute = (rideId: string) => `#/?chat=${encodeURIComponent(rideId)}`;
export const threadRoute = (threadId: string) =>
  `#/profile?thread=${encodeURIComponent(threadId)}`;

export const supported = () => typeof Notification !== 'undefined';

const granted = new Set<() => void>();

/**
 * Hears the passenger allow notifications, which is when this browser can first
 * be subscribed to Web Push. Returns the way to stop hearing.
 */
export function onGranted(heard: () => void): () => void {
  granted.add(heard);
  return () => granted.delete(heard);
}

/**
 * Asks once whether the app may notify. Called from something the passenger just
 * pressed — opening the chat, placing an order — because a browser shows the question
 * only to somebody who is doing something, and an answer asked for out of nowhere is
 * usually no. Safari on an iPhone also subscribes to push only from such a press, and
 * the answer is how that happens.
 */
export function askToNotify(): void {
  if (!supported() || Notification.permission !== 'default' || !alertsOn()) return;
  void Notification.requestPermission()
    .then((answer) => {
      if (answer === 'granted') granted.forEach((heard) => heard());
    })
    .catch(() => {
      // Refused, or a browser that asks another way. Either way the dot still works.
    });
}

/**
 * One notification, replacing whatever `tag` held before, so a chat is one entry
 * rather than a stack of them. Quiet about failing: a notification is a courtesy, and
 * the dot says the same thing in the app.
 *
 * `silent` is for an event the page is sounding itself, in the passenger's own choice
 * (ADR 0031): the system's sound as well would be the same news said twice.
 */
export function notify(options: {
  title: string;
  body: string;
  tag: string;
  go: string;
  silent?: boolean;
}): void {
  if (!supported() || Notification.permission !== 'granted' || !alertsOn()) return;
  const { title, body, tag, go, silent = false } = options;
  const shown = { body, tag, silent, icon: '/icon-192.png', data: { go } };

  const fromPage = () => {
    try {
      const note = new Notification(title, shown);
      note.onclick = () => {
        window.focus();
        follow(go);
        note.close();
      };
    } catch {
      // Chrome on Android, with no worker to go through.
    }
  };

  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
    fromPage();
    return;
  }
  void navigator.serviceWorker.ready
    .then((worker) => worker.showNotification(title, shown))
    .catch(fromPage);
}

/** Takes back what `tag` said: the conversation has been opened. */
export function withdraw(tag: string): void {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) return;
  void navigator.serviceWorker.ready
    .then((worker) => worker.getNotifications({ tag }))
    .then((notes) => notes.forEach((note) => note.close()))
    .catch(() => {});
}

/** Goes where a tapped notification leads. */
export function follow(go: string): void {
  if (go.startsWith('#/')) window.location.hash = go.slice(1);
}

/**
 * Hears the worker say a notification was tapped. Returns the way to stop hearing.
 */
export function followTaps(): () => void {
  if (!('serviceWorker' in navigator)) return () => {};
  const heard = (event: MessageEvent) => {
    const data = event.data as { type?: string; go?: string } | null;
    if (data?.type === 'notification-tap' && typeof data.go === 'string') follow(data.go);
  };
  navigator.serviceWorker.addEventListener('message', heard);
  return () => navigator.serviceWorker.removeEventListener('message', heard);
}

/** The tag a ride's chat and an Обращение are shown under. */
export const chatTag = (rideId: string) => `chat:${rideId}`;
export const threadTag = (threadId: string) => `support:${threadId}`;
/**
 * «Заказ принят», and «Водитель выехал к вам» in its place once a предзаказ's car sets
 * off — which only ever comes by push.
 */
export const rideTag = (rideId: string) => `ride:${rideId}`;

/**
 * «Пока не удалось найти водителя». Not the ride's own tag, for the reason a cancel's
 * is not: nothing is coming to take it back.
 */
export const notFoundTag = (rideId: string) => `notfound:${rideId}`;

/**
 * «Водитель отказался — ищем другого». Not the ride's own tag, which is taken back
 * whenever no car is on its way — which, until somebody else takes it, none is.
 */
export const refusedTag = (rideId: string) => `refused:${rideId}`;

/**
 * «Водитель отменил заказ № 1042». Not the ride's own tag: that one is taken back
 * whenever the car is not on its way, which after this it never is.
 */
export const cancelledTag = (rideId: string) => `cancelled:${rideId}`;

/**
 * Takes back «Заказ принят» and «Водитель выехал к вам» about every ride not in
 * [onTheWay]: the car has arrived, or the ride is over. A push put it there with no
 * tab open, and no push takes it back — a push must show something, and browsers stop
 * delivering to a site that sends ones that do not — so the first ride list the app
 * gets is the first chance to say it is no longer so.
 */
export function withdrawArrived(onTheWay: ReadonlySet<string>): void {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) return;
  void navigator.serviceWorker.ready
    .then((worker) => worker.getNotifications())
    .then((notes) => {
      for (const note of notes) {
        const ride = note.tag.startsWith('ride:') ? note.tag.slice('ride:'.length) : null;
        if (ride !== null && !onTheWay.has(ride)) note.close();
      }
    })
    .catch(() => {});
}

/** What a message says in a notification when it is not words. */
export function preview(message: { text?: string | null; kind?: string; storageKey?: string | null }) {
  const said = (message.text ?? '').trim();
  if (said) return said;
  if (message.kind === 'VOICE') return 'Голосовое сообщение';
  if (message.storageKey) return 'Фото';
  return 'Сообщение';
}

type Note = Parameters<typeof notify>[0];

const routeOf = (stops: readonly { address: string }[]) =>
  `${stops[0]?.address ?? ''} → ${stops[stops.length - 1]?.address ?? ''}`;

/**
 * What a ride's change from `was` is worth a notification, if anything (ADR 0032): a
 * driver took it, or nobody did before the search ran out. Word for word what the
 * server pushes — `acceptedNote` and `notFoundNote` in `server/src/push/notes.ts` —
 * under the same tags, so a tab and a push replace each other rather than stack.
 */
export function rideNote(
  was: RideStatus,
  ride: Pick<PassengerRideView, 'id' | 'status' | 'scheduledFor' | 'promiseMinutes' | 'stops'>,
): Note | null {
  if (moment(was, ride) === ACCEPTED) {
    return {
      title: 'Заказ принят',
      body:
        ride.scheduledFor === null && ride.promiseMinutes !== null
          ? `Машина будет примерно через ${ride.promiseMinutes} мин`
          : routeOf(ride.stops),
      tag: rideTag(ride.id),
      go: '#/',
      // «Заказ принят» has just been heard, in the sound the passenger chose.
      silent: true,
    };
  }
  if (was === 'SEARCHING' && ride.status === 'EXPIRED') {
    // A предзаказ nobody took by its time is over, not paused: there is no «Повторить
    // поиск» for it, only «История», under «Поездки».
    const preorder = ride.scheduledFor !== null;
    return {
      title: preorder ? 'Не удалось найти водителя' : 'Пока не удалось найти водителя',
      body: routeOf(ride.stops),
      tag: notFoundTag(ride.id),
      go: preorder ? '#/orders' : '#/',
    };
  }
  return null;
}

/**
 * Watches the ride list go past and says which changes in it are news — [rideNote]
 * for each ride that moved. Against what this tab last knew, as `RideSounds` is: the
 * first list is the tab catching up, and a ride seen for the first time was placed a
 * moment ago.
 */
export class RideNews {
  private seen = new Map<string, RideStatus>();
  private caughtUp = false;

  saw(rides: readonly Parameters<typeof rideNote>[1][]): Note[] {
    const before = this.seen;
    this.seen = new Map(rides.map((ride) => [ride.id, ride.status]));
    if (!this.caughtUp) {
      this.caughtUp = true;
      return [];
    }
    const news: Note[] = [];
    for (const ride of rides) {
      const was = before.get(ride.id);
      const note = was === undefined ? null : rideNote(was, ride);
      if (note) news.push(note);
    }
    return news;
  }

  /** The session ended: whoever signs in next starts from their own first list. */
  reset(): void {
    this.seen = new Map();
    this.caughtUp = false;
  }
}
